import { datadogLogs } from '@datadog/browser-logs';
import cornerstone from 'cornerstone-core';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';
import dayjs from 'dayjs';
import dicomParser from 'dicom-parser';

import { EventStream, IEventStreamConsumer, MIME_TYPES, equalsInsensitive, getFileExtension, hasText, lookupMimeType } from 'core/utils';

import {
  DATE_PARSE_FORMATS,
  DICOMDIR_TAG,
  DICOM_MINIMUM_FILE_SIZE,
  DICOM_TAG_LOOKUPS,
  DICOM_UNCOMPRESSED_TRANSFER_SYNTAXES,
  NORMALIZED_DATE_FORMAT,
  NORMALIZED_TIME_FORMAT,
  PARSE_DICOM_CARET_DELIMITED_TAGS,
  PARSE_DICOM_DATE_TAGS,
  PARSE_DICOM_METADATA_FILE_DATA_OPTIONS,
  PARSE_DICOM_METADATA_OPTIONS,
  PARSE_DICOM_TAGS,
  PARSE_DICOM_TIME_TAGS,
  TIME_PARSE_FORMATS,
  UNCOMPRESSED_DICOM_WARNING_MINIMUM_SIZE,
} from '../constants';
import { DicomDataDictionary } from '../dicom-data-dictionary';
import { FileUploadContext, ParseCompleteEvent, ParsedDicomMetadata, ProcessJobEvent, UploadLockRequest, UploadLockRequestType } from '../types';
import { ImageDataCache } from './ImageDataCache';
import { PriorityLock } from './PriorityLock';

const cornerstoneConfig = {
  maxWebWorkers: navigator.hardwareConcurrency || 1,
  startWebWorkersOnDemand: true,
  taskConfiguration: {
    decodeTask: {
      initializeCodecsOnStartup: false,
    },
  },
};

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
cornerstoneWADOImageLoader.webWorkerManager.initialize(cornerstoneConfig);
cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
cornerstoneWADOImageLoader.external.dicomParser = dicomParser;

export class FileScanner {
  static [Symbol.toStringTag]() {
    return 'FileScanner';
  }

  /** The key is the SeriesInstanceUID.  The value is the browser generated random id assigned to a series. */
  private _dicomSeriesIdLookup = new Map<string, string>();

  private _uploadSessionId: string | null = null;

  private _streams = {
    onParseComplete: new EventStream<ProcessJobEvent<ParseCompleteEvent>>(),
  };

  public get streams(): {
    onParseComplete: IEventStreamConsumer<ProcessJobEvent<ParseCompleteEvent>>;
  } {
    return this._streams;
  }

  /** DICOM file scanner and thumbnail generator.
   * @param _thumbnailCache The cache used to store generated thumbnails.
   * @param _dicomCache The cache used to store parsed DICOM metadata.  The fileId is the key.
   */
  constructor(private _thumbnailCache: ImageDataCache) {
    this.initialize = this.initialize.bind(this);
    this.reset = this.reset.bind(this);
    this.process = this.process.bind(this);
    this.scanNonDicomFile = this.scanNonDicomFile.bind(this);
    this.scanDicomFile = this.scanDicomFile.bind(this);
    this.extractDicomMetadata = this.extractDicomMetadata.bind(this);
    this._log = this._log.bind(this);
  }

  public initialize(uploadSessionId: string) {
    // Treat the FileScanner as already initialized and leave it unchanged if it has already been initialized.
    if (this._uploadSessionId != null) throw new Error('FileScanner has already been initialized.  Call reset() before initializing again.');

    this._uploadSessionId = uploadSessionId;
  }

  public reset() {
    // Treat a null uploadSessionId as indicitative of the FileScanner not being initialized or having been already reset, and therefore we do not need to do anything.
    if (this._uploadSessionId == null) return;

    this._uploadSessionId = null;
    this._dicomSeriesIdLookup.clear();
  }

  public process(file: Readonly<FileUploadContext>, lock: PriorityLock<UploadLockRequest>) {
    if (file.classification === 'dicom') {
      // Updated call with fileId and file parameters
      this.scanDicomFile(file.fileId, file.file, lock);
    } else {
      this.scanNonDicomFile(file.fileId, file.file);
    }
  }

  private scanNonDicomFile(fileId: string, file: File) {
    // Attempt to get a canonical mime type for the file.  This will help normalize things like capitilization and things like .JPG vs .JPEG.
    // Helpful because the backend does a lot of hardcoding for things like this.
    const mime = lookupMimeType(file.name);

    this._streams.onParseComplete.emit({
      result: 'processed',
      fileId,
      file,
      dicomData: null,
      uncompressedDicomWarning: false,
      startTime: performance.now(),
      duration: 0,
      fileType: mime?.extensions?.[0] ?? getFileExtension(file.name),
      didReadFile: false,
    });
  }

  private async scanDicomFile(fileId: string, file: File, lock: PriorityLock<UploadLockRequest>) {
    if (this._uploadSessionId == null) throw new Error('Cannot scan a DICOM file without an upload session ID.');

    // DICOM files have a minimum size requirement.  Skip any attempts at small files to increase read performance and reduce errors.
    if (file.size < DICOM_MINIMUM_FILE_SIZE) {
      this._log('info', `Skipping file because it is less than ${DICOM_MINIMUM_FILE_SIZE} bytes which is the minimum viable DICOM file.`, { fileId }, file);
      this._streams.onParseComplete.emit({ result: 'skipped', fileId });
      return;
    }

    // DICOMDIR files are not useful and they frequently cause errors during parsing.  Skip them.
    if (equalsInsensitive(file.name, 'DICOMDIR') || equalsInsensitive(file.name, 'DICOMDIR.dcm')) {
      this._log('info', 'DICOMDIR file detected.  Skipping.', { fileId }, file);
      this._streams.onParseComplete.emit({ result: 'skipped', fileId });
      return;
    }

    let releaseLock: (() => void) | undefined;

    try {
      let endMark: number | null = null;
      let measure: PerformanceMeasure | null = null;
      let success = false;

      releaseLock = await lock.requestLock({ type: UploadLockRequestType.ScanFile });

      const startMark = performance.now();

      try {
        const dicomData = await this.extractDicomMetadata(fileId, file);

        endMark = performance.now();

        success = true;

        // Skip files that are not DICOM.
        if (dicomData == null) {
          this._log('info', 'File determined as non-DICOM or unparseable by extractDicomMetadata, skipping.', { fileId }, file);
          this._streams.onParseComplete.emit({ result: 'skipped', fileId });
          return;
        }

        if (dicomData.DirectoryRecordSequence) {
          this._log('info', 'DICOMDIR file detected (parsed as DirectoryRecordSequence). Skipping.', { fileId }, file);
          this._streams.onParseComplete.emit({ fileId, result: 'skipped' });
          return;
        }

        // Check if the file is an uncompressed DICOM file.  We only want to warn the user if the file is relatively large.
        const uncompressedDicomWarning =
          file.size >= UNCOMPRESSED_DICOM_WARNING_MINIMUM_SIZE && DICOM_UNCOMPRESSED_TRANSFER_SYNTAXES.includes(dicomData.TransferSyntaxUID!);

        let seriesId = this._dicomSeriesIdLookup.get(dicomData.SeriesInstanceUID!) ?? null;

        if (seriesId == null && hasText(dicomData.SeriesInstanceUID)) {
          seriesId = crypto.randomUUID();
          this._dicomSeriesIdLookup.set(dicomData.SeriesInstanceUID, seriesId);
          this._thumbnailCache.set(seriesId, null);
        }

        measure = performance.measure(`parse-dicom:${fileId}`, {
          detail: {
            fileId,
            fileName: file.name,
            fileSize: file.size,
            devtools: {
              dataType: 'track-entry',
              trackGroup: 'Upload Pipeline',
              track: 'DICOM Parsing',
              color: 'primary',
              properties: [
                ['fileId', fileId],
                ['fileName', file.name],
                ['fileSize', file.size],
              ],
            },
          },
          start: startMark,
          end: endMark,
        });

        datadogLogs.logger.info('parse-dicom', {
          uploadSessionId: this._uploadSessionId,
          fileId,
          date: performance.timeOrigin + startMark,
          end_date: performance.timeOrigin + startMark + measure.duration,
          duration: measure.duration * 1000000, // Datadog expects durations to be in nanoseconds.
          success,
        });

        this._streams.onParseComplete.emit({
          result: 'processed',
          fileId,
          file,
          dicomData: dicomData,
          uncompressedDicomWarning,
          startTime: performance.timeOrigin + startMark,
          duration: endMark - startMark,
          fileType: MIME_TYPES.DCM.extensions[0],
          didReadFile: true,
        });
      } catch (error) {
        // File is not DICOM.  For now our intendend behavior is to skip such files.  This may change in the future.
        const actualError = error instanceof Error ? error : new Error(String(error ?? 'Unknown error during scanDicomFile processing'));
        this._log('error', 'Error processing DICOM file in scanDicomFile.', { fileId, errorDetails: actualError.message }, actualError, file);
        this._streams.onParseComplete.emit({ result: 'skipped', fileId });
      }
    } finally {
      releaseLock?.();
    }
  }

  private async readFileSlice(file: File, start: number, length: number): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result instanceof ArrayBuffer) {
          resolve(new Uint8Array(event.target.result));
        } else {
          reject(new Error('Unexpected result type. Expected ArrayBuffer.'));
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file.slice(start, start + length));
    });
  }

  private async extractDicomMetadata(fileId: string, file: File): Promise<ParsedDicomMetadata | null> {
    if (this._uploadSessionId == null) throw new Error('Cannot extract DICOM metadata without an upload session ID.');

    // Do an initial check to see if the file is a DICOM file based on the file extension.  This won't catch everything because
    // some DICOM files don't have a file extension.
    const fileExtension = getFileExtension(file.name);
    if (fileExtension != null && !(lookupMimeType('.dcm')?.extensions ?? []).some((ext) => equalsInsensitive(ext, fileExtension))) {
      return null;
    }

    // Attempt to read in "chunks" of the file to avoid unnecessarily reading the entire file into memory.  The performance hit of re-reading the same
    // file in gradually increasing chunks is negligible compared to the performance hit of disk I/O for a large file.  This is particularly true when
    // the file is on an optical drive.
    const chunkSizes = [Math.min(file.size, 4000)];
    if (file.size > 4000) {
      chunkSizes.push(Math.min(file.size, 16000));
    }
    if (file.size > 16000) {
      chunkSizes.push(Math.min(file.size, 64000));
    }
    if (file.size > 64000) {
      chunkSizes.push(file.size);
    }

    let dataSet = null;
    try {
      // Attempt a partial parse, reading until a specified tag.
      for (const chunkSize of chunkSizes) {
        try {
          if (chunkSizes[0] !== chunkSize) {
            this._log('info', 'Re-reading file with a larger chunk size.', { chunkSize, fileId }, file);
          }

          const readBuffer = await this.readFileSlice(file, 0, chunkSize);
          let attemptParse = true; // Flag to control if parsing should proceed

          // Check for DICM prefix before parsing
          if (chunkSize >= 132) {
            // Ensure buffer is large enough for the check
            const prefix = String.fromCharCode(readBuffer[128], readBuffer[129], readBuffer[130], readBuffer[131]);
            if (prefix !== 'DICM') {
              const warningMsg = "DICOM prefix 'DICM' not found at byte 128.";
              this._log(
                'warn',
                warningMsg,
                {
                  fileId,
                  chunkSize,
                  foundPrefix: prefix,
                },
                file,
              );
              dataSet = null;
              attemptParse = false; // Do not attempt to parse this chunk or any further for this file
              break; // Exit the chunk loop for this file
            }
          } else if (chunkSize > 0) {
            // Only log if we actually read some bytes but not enough for prefix
            this._log(
              'warn',
              'Read buffer too small to check for DICOM prefix.',
              {
                fileId,
                bufferLength: readBuffer.length,
                chunkSize,
              },
              file,
            );
            // For very small chunks, we still let attemptParse = true to give dicomParser a chance
          }

          if (attemptParse) {
            dataSet = dicomParser.parseDicom(readBuffer, {
              ...PARSE_DICOM_METADATA_OPTIONS,
              vrCallback: (tag) => {
                return DicomDataDictionary[FileScanner.formatDicomTagKey(tag, 'parenthesis')]?.vr;
              },
            });
          } else {
            // dataSet should already be null if attemptParse is false due to DICM prefix failure
            // This block primarily exists to make control flow clear if we add other reasons to skip parsing.
          }

          // If dataSet is null at this point (e.g. prefix check failed and broke, or parseDicom failed severely in a way not caught by try/catch below)
          // we should not proceed to check its elements.
          if (dataSet === null) {
            // If attemptParse was false, we already broke. If it was true and dataSet is still null here,
            // it implies parseDicom might have failed in an unexpected way before the try/catch for specific exceptions.
            // Or, it was the first chunk, too small for DICM check, and parseDicom failed to produce a dataset.
            // In any case, if no dataSet, try next chunk or fail if this was the last one.
            this._log(
              'info',
              'dataSet is null after parse attempt (or skipped due to prefix); trying next chunk or concluding.',
              {
                fileId,
                chunkSize,
                attemptParseStatus: attemptParse,
              },
              file,
            );
            // Ensure we try the next chunk if available, otherwise the loop will end and overall failure will be reported.
            continue;
          }

          // If we find the SeriesInstanceUID tag, we can assume it's a meaningful DICOM file.  If not then increase the chunk size and try again.
          if (dataSet.elements[DICOM_TAG_LOOKUPS.SeriesInstanceUID]) {
            break;
          } else {
            // Log that SeriesInstanceUID was not found with this chunk, but don't treat as an immediate error yet.
            this._log(
              'info',
              'SeriesInstanceUID not found with current chunk. Will try larger chunk if available.',
              {
                fileId,
                chunkSize,
              },
              file,
            );
            dataSet = null; // Clear dataSet to try next chunk or fail if this was the last chunk
          }
        } catch (err) {
          const parserError = err instanceof Error ? err.message : String(err);
          const exceptionString =
            err != null && typeof err === 'object' && 'exception' in err && typeof err.exception === 'string' ? err.exception : parserError;

          let partialDataSetSummary = 'N/A';
          if (err != null && typeof err === 'object' && 'dataSet' in err && err.dataSet != null) {
            const partialDs = err.dataSet as Partial<dicomParser.DataSet>; // Use a partial type if available or any
            if (partialDs.elements) {
              const successfullyParsedTags = Object.keys(partialDs.elements);
              partialDataSetSummary = `Partially parsed ${successfullyParsedTags.length} tags. First few: [${successfullyParsedTags.slice(0, 5).join(', ')}${successfullyParsedTags.length > 5 ? '...' : ''}]`;
            }
          }

          // Log details of the parsing attempt that failed for this specific chunk
          const logDetails = {
            fileId,
            chunkSize,
            error: exceptionString,
            partialDataSetSummary,
            originalErrorObject: err, // Keep for inspection if needed, console will show it
          };
          this._log('warn', 'DICOM parse attempt failed for chunk.', logDetails, err instanceof Error ? err : new Error(exceptionString), file);

          // If the error is a known retryable buffer error then we should attempt to increase the chunk size and try again.
          if (
            err != null &&
            typeof err === 'object' &&
            'exception' in err &&
            typeof err.exception === 'string' &&
            (err.exception.includes('buffer overrun') ||
              err.exception.includes('attempt to read past end of buffer') ||
              err.exception.includes("invalid value for parameter 'max")) &&
            chunkSize !== file.size
          ) {
            // console.log(`RETRYING CHUNK: Error identified for retry: "${err.exception}", current chunkSize: ${chunkSize}, file.size: ${file.size}`); // This was for specific test
            continue; // Try next chunk size
          }

          // If the error is not retryable OR it's the last chunk, this attempt is considered final for this path.
          // We don't return null immediately, let the loop finish or break if UID was found.
          // If loop finishes and dataSet is still null, then the outer "Could not parse" error will be logged.
          // For non-retryable errors, we effectively stop trying further chunks for this specific error type by not continuing.
          // However, to ensure we don't proceed with a potentially corrupt partial dataSet from a non-retryable error:
          dataSet = null; // Clear dataSet as this chunk definitively failed.
          break; // Exit the chunk loop as this error is not one we retry by increasing chunk size.
        }
      }

      if (dataSet == null) {
        // We couldn't find the SeriesInstanceUID, but presumably the file is DICOM since we are at this point.  Assume it's not usable, but this needs to be logged.
        this._log('error', 'Could not parse a data set from file after trying all chunks or due to non-retryable error.', { fileId }, file);
        return null;
      }

      const parsed: ParsedDicomMetadata = {
        AccessionNumber: null,
        BodyPartExamined: null,
        StudyDescription: null,
        PatientID: null,
        PatientBirthDate: null,
        PatientSex: null,
        PatientName: [],
        StudyDate: null,
        StudyTime: null,
        StudyInstanceUID: null,
        Modality: null,
        SeriesInstanceUID: null,
        SeriesDescription: null,
        TransferSyntaxUID: null,
        DirectoryRecordSequence: false,
      };

      for (const tagDef of PARSE_DICOM_TAGS) {
        const tagKeyInDataSet = FileScanner.formatDicomTagKey(tagDef.tag, 'leading-x');
        const element = dataSet.elements[tagKeyInDataSet];

        if (tagDef.tag === DICOMDIR_TAG.tag) {
          if (element) {
            parsed.DirectoryRecordSequence = true;
          }
          continue;
        }

        let valueStr: string | null = null;
        if (element && typeof element.length === 'number') {
          // Check if element and length exist
          try {
            const rawString = dataSet.string(tagKeyInDataSet);
            valueStr = rawString === undefined ? null : rawString;
          } catch (ex) {
            const error = ex instanceof Error ? ex : new Error(String(ex ?? 'Unknown error reading tag string'));
            this._log(
              'warn',
              `Failed to read string for DICOM tag ${tagDef.name} (${tagKeyInDataSet}): ${error.message}`,
              { fileId, dicomTag: tagDef.tag, tagName: tagDef.name, rawValueAttemptFailed: true },
              error,
              file,
            );
            // valueStr remains null, subsequent processing will handle it as missing/empty
          }
        }

        // Ensure the target property exists in parsed before assigning
        if (!(tagDef.name in parsed)) {
          throw new Error(`Could not find a matching parse destination property for tag: ${tagDef.tag} ${tagDef.name}.`);
        }

        if (PARSE_DICOM_CARET_DELIMITED_TAGS.some((t) => t.tag === tagDef.tag)) {
          const values = hasText(valueStr)
            ? valueStr
                .split('^')
                .map((v) => v.trim())
                .filter((v) => hasText(v))
            : [];
          if (tagDef.name === 'PatientName') {
            // PatientName is string[]
            parsed.PatientName = values;
          } else {
            // For other potential future caret-delimited tags that might be string[] or (string[] | null)
            // This example assumes they are nullable if not PatientName. Adjust if specific non-nullable array tags are added.
            Object.assign(parsed, { [tagDef.name]: values.length > 0 ? values : null });
          }
        } else if (PARSE_DICOM_DATE_TAGS.some((t) => t.tag === tagDef.tag)) {
          if (hasText(valueStr)) {
            const parsedDate = dayjs(valueStr, DATE_PARSE_FORMATS, true);
            if (parsedDate.isValid()) {
              Object.assign(parsed, { [tagDef.name]: parsedDate.format(NORMALIZED_DATE_FORMAT) });
            } else {
              this._log(
                'error',
                `Could not parse date for DICOM tag: ${tagDef.tag} ${tagDef.name}. Raw value: "${valueStr}"`,
                { fileId, dicomTag: tagDef.tag, dicomTagName: tagDef.name, rawValue: valueStr },
                file,
              );
              // Do not assign null to non-nullable date fields; they keep their default (null if nullable, or initial if non-nullable)
              // parsed[tagDef.name as keyof ParsedDicomMetadata] = null; // Removed this line
            }
          } else {
            // If valueStr is empty/null, and the field is nullable string, assign null.
            // All date tags in PARSE_DICOM_DATE_TAGS correspond to (string | null) properties in ParsedDicomMetadata.
            Object.assign(parsed, { [tagDef.name]: null });
          }
        } else if (PARSE_DICOM_TIME_TAGS.some((t) => t.tag === tagDef.tag)) {
          if (hasText(valueStr)) {
            const parsedTime = dayjs(valueStr.split('.')[0], TIME_PARSE_FORMATS, true);
            if (parsedTime.isValid()) {
              Object.assign(parsed, { [tagDef.name]: parsedTime.format(NORMALIZED_TIME_FORMAT) });
            } else {
              this._log(
                'error',
                `Could not parse time for DICOM tag: ${tagDef.tag} ${tagDef.name}. Raw value: "${valueStr}"`,
                { fileId, dicomTag: tagDef.tag, dicomTagName: tagDef.name, rawValue: valueStr },
                file,
              );
              // Do not assign null to non-nullable time fields
              // parsed[tagDef.name as keyof ParsedDicomMetadata] = null; // Removed this line
            }
          } else {
            // If valueStr is empty/null, and the field is nullable string, assign null.
            // All time tags in PARSE_DICOM_TIME_TAGS correspond to (string | null) properties in ParsedDicomMetadata.
            Object.assign(parsed, { [tagDef.name]: null });
          }
        } else {
          // Handles general string tags (nullable strings) and DirectoryRecordSequence (boolean)
          if (tagDef.name === 'DirectoryRecordSequence') {
            // This is handled by the initial check for element presence for DICOMDIR_TAG
            // If DICOMDIR_TAG was not found, DirectoryRecordSequence remains false (its default)
          } else {
            // All other tags are assumed to be (string | null) as per ParsedDicomMetadata
            Object.assign(parsed, { [tagDef.name]: hasText(valueStr) ? valueStr.trim() : null });
          }
        }
      }

      return parsed;
    } catch (exception) {
      const errorInstance = FileScanner.createError(exception, 'Error while reading file.');
      this._log('error', 'Error while reading file (extractDicomMetadata).', { fileId, originalExceptionMessage: errorInstance.message }, errorInstance, file);
      return null;
    }
  }

  /** Format a DICOM tag key.  Note that "leading-x" is produces all lowercase, but "parenthesis" results in all uppercase.  This is intentional and is required by third party libraries and our own logic that depend on exact casing for comparisons. */
  private static formatDicomTagKey(tagKey: string, outputFormat: 'leading-x' | 'parenthesis') {
    let group: string, element: string;

    if (tagKey.length === 11) {
      // Tags with format: (0020,000D)
      group = tagKey.substring(1, 5);
      element = tagKey.substring(6, 10);
    } else if (tagKey.length === 9) {
      // Tags with format: x0020000d
      group = tagKey.substring(1, 5);
      element = tagKey.substring(5, 9);
    } else {
      throw new Error(`Unable to determine DICOM tag format: "${tagKey}".`);
    }

    // Note the difference in capitalization between the two formats.  This is intentional due to third party library requirements.
    const result = outputFormat === 'leading-x' ? `x${group}${element}`.toLowerCase() : `(${group},${element})`.toUpperCase();

    return result;
  }

  /** Helper for producing actionable errors for logs.  The dicomParser library throws strings and objects that are not instances of Error which makes it difficult to understand what is happening in the logs.
   * @param error The error emitted by the dicomParser library.
   * @param defaultMessage The default error message to use if a suitable error message cannot be retrieved from the error object.
   * @returns An instance of Error.
   */
  private static createError(error: unknown, defaultMessage: string) {
    if (error instanceof Error) {
      return error;
    }

    if (typeof error === 'string') {
      return new Error(error);
    }

    if (error == null) {
      return new Error(defaultMessage);
    }

    if (typeof error === 'object' && 'exception' in error && typeof error.exception === 'string') {
      return new Error(error.exception, { cause: error });
    }

    return new Error(defaultMessage, { cause: error });
  }

  // Overload signatures
  private _log(level: 'info' | 'warn' | 'error', message: string, details: Record<string, unknown>): void;
  private _log(level: 'info' | 'warn' | 'error', message: string, details: Record<string, unknown>, file: File): void;
  private _log(level: 'info' | 'warn' | 'error', message: string, details: Record<string, unknown>, errorInstance: Error): void;
  private _log(level: 'info' | 'warn' | 'error', message: string, details: Record<string, unknown>, errorInstance: Error, file: File): void;
  // Implementation signature
  private _log(
    level: 'info' | 'warn' | 'error',
    message: string,
    details: Record<string, unknown>,
    param4?: Error | File, // Can be an Error instance or a File instance
    param5?: File, // Only used if param4 is an Error instance and a File is also provided
  ): void {
    let errorInstanceUsed: Error | undefined = undefined;
    let fileUsed: File | undefined = undefined;

    if (param4 instanceof Error) {
      errorInstanceUsed = param4;
      if (param5 instanceof File) {
        fileUsed = param5; // Called with (details, error, file)
      }
      // Else: Called with (details, error) - param5 is undefined
    } else if (param4 instanceof File) {
      fileUsed = param4; // Called with (details, file) - param5 must be undefined
    }
    // Else: Called with (details) - param4 and param5 are undefined

    const fileDetails = fileUsed ? { fileName: fileUsed.name, fileSize: fileUsed.size } : {};
    const logContext = { uploadSessionId: this._uploadSessionId, ...fileDetails, ...details };

    switch (level) {
      case 'info':
        console.info(message, logContext);
        datadogLogs.logger.info(message, logContext);
        break;
      case 'warn':
        console.warn(message, logContext, errorInstanceUsed ?? ''); // Pass errorInstance if available to console
        datadogLogs.logger.warn(message, logContext, errorInstanceUsed);
        break;
      case 'error': {
        console.error(message, logContext, errorInstanceUsed ?? ''); // Pass errorInstance if available to console
        // Ensure Datadog always gets an Error object for its 'error' level
        const datadogError = errorInstanceUsed ?? new Error(message);
        datadogLogs.logger.error(message, logContext, datadogError);
        break;
      }
    }
  }
}
